/* eslint-disable react-refresh/only-export-components -- exportamos
   hooks + providers co-localizados; HMR no crítico en este archivo. */
/**
 * Contexto del municipio activo (Sprint 7).
 *
 * Reemplaza los hardcodes `ACTIVE_MUNICIPALITY_DANE = "17486"` que estaban
 * repartidos en Radar, Expedientes, FichaMunicipal, Alertas y
 * OportunidadesPerdidas.
 *
 * El contexto consume `/onboarding/context` cuando el usuario está
 * autenticado. Expone:
 * - `daneCode` y `name` del muni activo (si hay).
 * - `needsOnboarding` — true si el usuario no tiene tenant o aún no terminó
 *   el wizard (onboarded_at IS NULL). Se usa en el router para redirigir.
 * - `tenantStatus`/`subscriptionState`/`tier`/`tierPeriod`/`memberRole`/
 *   `seatsUsed`/`seatsLimit` (Sprint 25-2) — espejo en camelCase de los
 *   mismos campos del DTO, para consumidores que no necesitan el objeto
 *   `context` completo. `OnboardingGate` lee el `tenant_status` crudo de
 *   `context` (lo pasa tal cual a `TenantPausado`); estos campos son para
 *   el resto de la UI (ej. un futuro panel de membresía).
 * - `refetch()` — para cuando el wizard avanza pasos.
 *
 * Si el usuario no está autenticado, el contexto devuelve `isLoading=false`
 * con todos los campos null — los ProtectedRoutes se encargan del redirect
 * a `/login`.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, useContext, useEffect, type ReactNode } from "react";

import { ApiError, fetchOnboardingContext, type OnboardingContextDTO } from "@/lib/api";

import { onAccessDenied } from "./access-denied";
import { useAuth } from "./auth";

interface MunicipalityContextValue {
  /** Código DANE del muni activo, o null si no hay. */
  daneCode: string | null;
  name: string | null;
  displayName: string | null;
  needsOnboarding: boolean;
  /**
   * True si `users.is_staff = TRUE` (equipo Kaptar — ADR-016).
   * Se usa para mostrar el AdminShell + sidebar admin y para bypass
   * del OnboardingGate cuando un staff puro no tiene tenant.
   */
  isStaff: boolean;
  /**
   * Estado operacional del tenant (`active` / `inactive` /
   * `pending_deletion`) — espejo de `context.tenant_status` (Sprint 25-2).
   */
  tenantStatus: string | null;
  /** Espejo de `context.subscription_state` — eje del embudo comercial. */
  subscriptionState: string | null;
  /** Espejo de `context.tier` (`esencial` / `pro`). */
  tier: string | null;
  /** Espejo de `context.tier_period` (`trimestral` / `semestral` / `annual` / legacy `monthly`). */
  tierPeriod: string | null;
  /**
   * Espejo de `context.tier_valid_until`: último día pagado como
   * `"YYYY-MM-DD"` (issue #734). Se lee con `fechaDesdeIso`.
   */
  tierValidUntil: string | null;
  /** Espejo de `context.member_role` del usuario en el tenant activo. */
  memberRole: string | null;
  /** Espejo de `context.seats_used`. */
  seatsUsed: number | null;
  /** Espejo de `context.seats_limit`. */
  seatsLimit: number | null;
  isLoading: boolean;
  error: Error | null;
  context: OnboardingContextDTO | null;
  /**
   * Refresca el contexto y devuelve la promesa REAL del refetch — no la
   * descartes con `void`. Un caller que hace `await refetch()` antes de
   * navegar (ej. `Onboarding.tsx`, `RenovarMembresia.tsx`) depende de que
   * esa promesa no resuelva hasta que la query haya terminado de verdad;
   * si se descarta, el `await` resuelve al instante y el caller navega
   * con datos todavia viejos en cache (bug encontrado en review, Sprint
   * 25-3 fix round 1 — `OnboardingGate` mostraba `TenantPausado` justo
   * despues de un pago exitoso porque `tenant_status` seguia `inactive`).
   */
  refetch: () => Promise<unknown>;
}

const Ctx = createContext<MunicipalityContextValue | null>(null);

/**
 * Provider con valor inyectado — útil en tests para saltarse el fetch.
 * En producción usar `MunicipalityProvider` que consume `/onboarding/context`.
 */
export function MunicipalityTestProvider({
  value,
  children,
}: {
  value: Partial<MunicipalityContextValue>;
  children: ReactNode;
}) {
  const full: MunicipalityContextValue = {
    daneCode: null,
    name: null,
    displayName: null,
    needsOnboarding: false,
    isStaff: false,
    tenantStatus: null,
    subscriptionState: null,
    tier: null,
    tierPeriod: null,
    tierValidUntil: null,
    memberRole: null,
    seatsUsed: null,
    seatsLimit: null,
    isLoading: false,
    error: null,
    context: null,
    refetch: () => Promise.resolve(),
    ...value,
  };
  return <Ctx.Provider value={full}>{children}</Ctx.Provider>;
}

/**
 * Tiempo mínimo entre dos relecturas del contexto provocadas por 403. Una
 * pantalla como el Radar dispara varias peticiones a la vez; todas fallan
 * juntas y basta una relectura.
 */
export const ACCESS_DENIED_COOLDOWN_MS = 3000;

export function MunicipalityProvider({ children }: { children: ReactNode }) {
  const { session, isLoading: authLoading } = useAuth();
  const queryClient = useQueryClient();
  const userId = session?.user?.id ?? null;

  // 1-oct-2026: si el servidor niega el acceso (403) a una pantalla ya abierta,
  // vuelve a pedir el contexto. Si dice `access_revoked`, `OnboardingGate` manda
  // al usuario a /login. Sin esto, quien navega dentro de la misma ventana
  // después de que lo quitan sigue viendo la app hasta que recarga.
  useEffect(() => {
    if (!userId) return;
    let ultima = 0;
    return onAccessDenied((url) => {
      // El 403 del propio contexto no se vuelve a escuchar: sería un bucle.
      if (url.includes("/onboarding/context")) return;
      const ahora = Date.now();
      if (ahora - ultima < ACCESS_DENIED_COOLDOWN_MS) return;
      ultima = ahora;
      void queryClient.invalidateQueries(
        { queryKey: ["onboarding-context", userId] },
        { cancelRefetch: false },
      );
    });
  }, [userId, queryClient]);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["onboarding-context", session?.user?.id ?? null],
    queryFn: fetchOnboardingContext,
    enabled: Boolean(session),
    // 7-sep-2026: el contexto es lo que dice si el usuario sigue teniendo
    // acceso (`access_revoked`, tenant pausado). Al volver a la pestaña se
    // revalida, para que a quien quitaron del equipo no le siga
    // respondiendo la app hasta que recargue a mano. Excepción deliberada
    // al `refetchOnWindowFocus: false` global de App.tsx.
    refetchOnWindowFocus: true,
    retry: (count, err) => {
      if (err instanceof ApiError && err.status === 401) return false;
      return count < 2;
    },
  });

  const needsOnboarding = (() => {
    if (!session) return false;
    if (!data) return false;
    // Sin tenant o sin onboarded_at → hay que mostrar el wizard.
    return !data.has_tenant || !data.onboarded_at;
  })();

  const value: MunicipalityContextValue = {
    daneCode: data?.active_dane_code ?? null,
    name: data?.active_municipality_name ?? null,
    displayName: data?.active_tenant_display_name ?? null,
    needsOnboarding,
    isStaff: data?.is_staff ?? false,
    tenantStatus: data?.tenant_status ?? null,
    subscriptionState: data?.subscription_state ?? null,
    tier: data?.tier ?? null,
    tierPeriod: data?.tier_period ?? null,
    tierValidUntil: data?.tier_valid_until ?? null,
    memberRole: data?.member_role ?? null,
    seatsUsed: data?.seats_used ?? null,
    seatsLimit: data?.seats_limit ?? null,
    isLoading: authLoading || (Boolean(session) && isLoading),
    error: error instanceof Error ? error : null,
    context: data ?? null,
    // Devuelve la promesa real de TanStack Query — NUNCA envolver en
    // `void refetch()` (eso descarta la promesa y hace que cualquier
    // `await refetch()` de un caller resuelva antes de que la query
    // termine; ver el docstring de `refetch` en la interfaz).
    refetch: () => refetch(),
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/**
 * Hook principal — devuelve el muni activo.
 *
 * En páginas que REQUIEREN muni activo (Radar, Expedientes, etc.), el
 * router ya garantiza que `daneCode` no sea null porque redirige al
 * onboarding cuando `needsOnboarding`. Aún así retornamos `string | null`
 * para que TypeScript force verificar.
 */
export function useMunicipality(): MunicipalityContextValue {
  const ctx = useContext(Ctx);
  if (!ctx) {
    throw new Error("useMunicipality debe usarse dentro de <MunicipalityProvider>");
  }
  return ctx;
}

/**
 * Variante estricta — tira error si no hay muni activo.
 * Útil en páginas protegidas donde el router ya garantizó el contexto.
 */
export function useActiveMunicipalityDane(): string {
  const { daneCode } = useMunicipality();
  if (!daneCode) {
    throw new Error("No hay municipio activo — esta página requiere completar el onboarding.");
  }
  return daneCode;
}
